<template>
    <div>
        <div class="mb-8 -ml-4 w-[calc(100%+32px)] lg:-ml-8 lg:w-[calc(100%+64px)] relative">
            <div class="w-full absolute h-0.5 bg-slate-300 inset-s-0 inset-bs-4"></div>
            <div class="w-full absolute h-px bg-white inset-s-0 inset-bs-4"></div>
            <button @click.prevent="choosingDelay = true"
                data-test="choose-delay-button"
                type="button"
                class="p-0 font-sans mx-8 relative h-8 rounded-full shadow-[0px_1px_0px_0px_#fff] border border-solid border-slate-300 bg-slate-100 cursor-pointer transition hover:bg-slate-200 ">
                <div
                    class="rounded-full px-4 w-full h-full border-solid text-slate-400 border-t border-white border-x-0 border-b-0 flex justify-center items-center gap-2">
                    <font-awesome-icon widthAuto :icon="faClock"></font-awesome-icon>
                    <span v-if="!choosingDelay && props.modelValue.delay_amount && props.modelValue.delay_type"
                        class="text-sm text-slate-800">
                        Wait {{ pluralized(props.modelValue.delay_type, props.modelValue.delay_amount, true) }} then
                        continue
                    </span>
                </div>
            </button>

            <div v-if="choosingDelay"
                class="px-4 -mt-4 pb-6 pt-8 bg-slate-200 lg:px-8 ">
                <div class="text-sm mb-4">
                    How long should we wait before sending the next message?
                </div>
                <div class="flex flex-col lg:items-center gap-2 lg:flex-row lg:gap-4">
                    <div>
                        <vx-input
                            data-test="delay-amount-input"
                            size="lg"
                            type="number"
                            :min="0"
                            :step="1"
                            :modelValue="props.modelValue.delay_amount.toString() || 0"
                            @update:modelValue="updateModelValue('delay_amount', parseInt($event, 10) ? parseInt($event, 10) : 0)"></vx-input>
                    </div>
                    <div>
                        <vx-tabs data-test="delay-type-tabs"
                            class="lg:shrink-0"
                            :disabled="props.disabled"
                            size="lg"
                            :modelValue="props.modelValue.delay_type"
                            @update:modelValue="updateModelValue('delay_type', $event)">
                            <vx-tab value="minutes">Minutes</vx-tab>
                            <vx-tab value="hours">Hours</vx-tab>
                            <vx-tab value="days">Days</vx-tab>
                        </vx-tabs>
                    </div>
                    <div>
                        <vx-button @click.prevent="choosingDelay = false"
                            data-test="choose-delay-confirm"
                            type="button"
                            class="w-full lg:w-auto"
                            size="lg"
                            color="primary">
                            Confirm
                            <font-awesome-icon widthAuto :icon="faCheck"></font-awesome-icon>
                        </vx-button>
                    </div>
                </div>

            </div>

        </div>
        <div class="shadow rounded-lg border border-solid border-white">
            <div @click.prevent="open = props.total === 1 ? true : !open"
                class="flex rounded-t-lg justify-between items-center px-4 py-3 cursor-pointer border-t-0 border-x-0 border-b border-solid"
                :class="{
                    'bg-slate-100 border-b-slate-200': !props.campaignId,
                    'bg-sky-50 border-b-sky-100': props.modelValue.status === 'published' && props.campaignId,
                    'bg-slate-50 border-b-slate-100': props.modelValue.status === 'draft' && props.campaignId,
                }">
                <div>
                    <vx-badge v-if="!props.campaignId"
                        color="primary"
                        size="xs">New Message</vx-badge>
                    <template v-else>
                        <vx-toggle size="sm" :modelValue="props.modelValue.status === 'published'"
                            @update:modelValue="updateModelValue('status', $event ? 'published' : 'draft')">
                            <template v-slot:no>Inactive</template>
                            <template v-slot:yes>Active</template>
                        </vx-toggle>
                    </template>
                </div>

                <font-awesome-icon widthAuto :icon="open ? faChevronUp : faChevronDown"
                    :class="{
                        'opacity-35': props.total === 1
                    }"></font-awesome-icon>
            </div>
            <div class="px-8 bg-white rounded-b-lg">
                <div v-if="!open"
                    class="py-4">
                    <div class="line-clamp-2"
                        :class="{
                            'text-slate-700': props.modelValue.body,
                            'text-slate-500 italic': !props.modelValue.body,
                        }">{{ props.modelValue.body || 'Your message here...' }}</div>
                    <div v-if="displayFirstError(validation.body.$errors)"
                        class="text-xs text-rose-800 mt-2">{{ displayFirstError(validation.body.$errors) }}</div>
                </div>
                <div v-else
                    class="py-8">
                    <message-builder ref="messageBuilderRef"
                        testable
                        data-test="message-builder"
                        placeholder="Your message here..."
                        :modelValue="props.modelValue.body"
                        :mediaContentType="props.modelValue.media_content_type"
                        :mediaUrl="props.modelValue.media_url"
                        @update:modelValue="updateModelValue('body', $event)"
                        @update:mediaContentType="updateModelValue('media_content_type', $event)"
                        @update:mediaUrl="updateModelValue('media_url', $event)"
                        :error="displayFirstError(validation.body.$errors)"
                        ></message-builder>

                    <div class="mt-8 text-slate-700 flex flex-col gap-2 text-sm">
                        <div class="p-6 bg-slate-50 rounded-2xl">
                            <div class="justify-between items-center flex">
                                <div>
                                    Message should be limited to a <strong>specific timeframe</strong>?
                                </div>
                                <div>
                                    <vx-toggle size="sm" data-test="should-send-on-schedule-toggle"
                                        :modelValue="!!props.modelValue.should_send_on_schedule"
                                        @update:modelValue="updateModelValue('should_send_on_schedule', $event)"></vx-toggle>
                                </div>
                            </div>
                            <div v-if="props.modelValue.should_send_on_schedule"
                                class="mt-6">
                                <div><strong>What days should this message send?</strong></div>
                                <div class="mt-4 mb-6 text-sm">
                                    <div class="flex gap-4 items-center">
                                        <div v-for="day in days"
                                            class="text-center"
                                            :key="'campaign_message.' + props.modelValue.id + '.' + day.key">
                                            <vx-checkbox size="lg"
                                                :data-test="day.key + '-checkbox'"
                                                :modelValue="props.modelValue[day.key]"
                                                @update:modelValue="updateModelValue(day.key, $event)"></vx-checkbox>
                                            <div class="mt-1">{{ day.label }}</div>
                                        </div>
                                    </div>

                                </div>
                                <div>
                                    <strong>Between what times should this message send?</strong> <br />
                                    This will use the recipient’s time zone.
                                </div>
                                <div class="flex items-center gap-4 mt-4">
                                    <div class="w-36" data-test="send-at-time">
                                        <vx-selectable size="lg"
                                            :clearable="false"
                                            :options="timeOptions"
                                            :reduce="option => option.value"
                                            :modelValue="props.modelValue.send_at_time"
                                            @update:modelValue="updateModelValue('send_at_time', $event)">
                                        </vx-selectable>
                                    </div>
                                    <font-awesome-icon widthAuto :icon="faArrowsLeftRight"
                                        class="text-slate-400"></font-awesome-icon>
                                    <div class="w-36" data-test="send-stop-time">
                                        <vx-selectable size="lg"
                                            :clearable="false"
                                            :options="timeOptions"
                                            :reduce="option => option.value"
                                            :modelValue="props.modelValue.send_stop_time"
                                            @update:modelValue="updateModelValue('send_stop_time', $event)">
                                        </vx-selectable>
                                    </div>
                                </div>

                            </div>
                        </div>
                        <div class="p-6 bg-slate-50 rounded-2xl">
                            <div class="justify-between items-center flex">
                                <div>
                                    Should this message <strong>collect data</strong>?
                                </div>
                                <div>
                                    <vx-toggle size="sm" data-test="dynamic-field-toggle"
                                        :modelValue="!!props.modelValue.dynamic_field"
                                        @update:modelValue="updateModelValue('dynamic_field', $event ? {} : null)"></vx-toggle>
                                </div>
                            </div>

                            <div v-if="props.modelValue.dynamic_field"
                                class="">
                                <div class="text-slate-500 my-6">You can save specific data like a person’s name or
                                    email when they respond to this message. Select from the list below to see options.
                                </div>
                                <dynamic-field-extraction :fieldType="props.modelValue.dynamic_field.type"
                                    @update:fieldType="updateDynamicField('type', $event)"
                                    :customAttribute="props.modelValue.dynamic_field.custom_attribute_name"
                                    @update:customAttribute="updateDynamicField('custom_attribute_name', $event)"
                                    :choices="props.modelValue.dynamic_field.choices"
                                    @update:choices="updateDynamicField('choices', $event)"
                                    :multipleChoices="props.modelValue.dynamic_field.multiple_choices"
                                    @update:multipleChoices="updateDynamicField('multiple_choices', $event)"
                                    ></dynamic-field-extraction>
                            </div>
                        </div>

                    </div>
                    <vx-button v-if="props.total !== 1"
                        class="mt-8"
                        @click.prevent="emit('remove')"
                        type="button"
                        color="muted-light"
                        size="sm">
                        Delete Message <font-awesome-icon widthAuto :icon="faTrash"></font-awesome-icon>
                    </vx-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { faClock } from '@fortawesome/pro-regular-svg-icons';
import { faArrowsLeftRight, faCheck, faChevronDown, faChevronUp, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxBadge, VxButton, VxCheckbox, VxInput, VxSelectable, VxTab, VxTabs, VxToggle } from '@voxie/frontend-components';
import useVuelidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { computed, ref, watch } from 'vue';
import DynamicFieldExtraction from '~/components/dynamic-fields/DynamicFieldExtraction.vue';
import { pluralized } from '~/components/filters';
import MessageBuilder from '~/components/general/message-builder/MessageBuilder.vue';
import { timeOfDayOptions } from '~/utils/date';
import { displayFirstError } from '~/utils/validation';

const props = defineProps({
    campaignId: {
        type: [Number, undefined],
        required: false,
    },
    modelValue: {
        type: Object,
        required: true,
    },
    total: {
        type: Number,
        default: 0,
    },
});

const emit = defineEmits(["update:modelValue", 'remove']);

const updateModelValue = (key, value) => {
    emit("update:modelValue", {
        ...props.modelValue,
        [key]: value,
    });
};

const updateDynamicField = (key, value) => {
    emit('update:modelValue', {
        ...props.modelValue,
        dynamic_field: {
            ...props.modelValue.dynamic_field,
            [key]: value,
        }
    })
}

const open = ref(props.total === 1 || !props.modelValue.body);

const choosingDelay = ref(false);

watch(() => props.total, () => {
    if (props.total === 1) {
        open.value = true;
    }
});

const days = [
    {
        key: 'send_on_0',
        label: 'Su',
    },
    {
        key: 'send_on_1',
        label: 'Mo',
    },
    {
        key: 'send_on_2',
        label: 'Tu',
    },
    {
        key: 'send_on_3',
        label: 'We',
    },
    {
        key: 'send_on_4',
        label: 'Th',
    },
    {
        key: 'send_on_5',
        label: 'Fr',
    },
    {
        key: 'send_on_6',
        label: 'Sa',
    },
];

const timeOptions = timeOfDayOptions().map((option) => ({ ...option, value: `${option.value}:00` }));

const messageBuilderRef = ref();

const validation = useVuelidate({
    body: {
        required,
    },
}, computed(() => props.modelValue));

defineExpose({
    focusMessage: () => messageBuilderRef.value?.textarea?.focus?.(),
    close: () => open.value = false
});

</script>
